import {Injectable, inject} from '@angular/core';
import {Auth, user} from '@angular/fire/auth';
import {Database, objectVal, ref} from '@angular/fire/database';
import {EMPTY, Observable, catchError, combineLatest, defer, firstValueFrom, map, of, shareReplay, switchMap} from 'rxjs';
import {AccessRequest, AccessRequests} from './models';

export interface Role {
  key: string;
  sort: number;
  title: string;
  description: string;
  permissions: string[];
  canManageRoles?: string[];
  eventOnly?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthzService {
  private readonly db = inject(Database);
  private readonly auth = inject(Auth);
  
  readonly roles$ = objectVal<{[key:string]: Role}>(ref(this.db, `/permissions/roles`))
    .pipe(map(obj => {
      const result = Object.values(obj || {});
      result.sort((a, b) => a.sort - b.sort);

      return result;
    }), 
    shareReplay(1),
  );

  readonly roleNames$ = this.roles$.pipe(map(roles => {
    const result: {[role: string]: string} = {};

    for(const r of roles) {
      result[r.key] = r.title;
    }

    return result;
  }), shareReplay(1));

  isAuthorized(regionKey: string, eventKey: string|undefined, permission: string): Observable<boolean> {
    return user(this.auth).pipe(
      switchMap(user => {
        if (user) {
          return combineLatest([
            objectVal(ref(this.db, `regions/${regionKey}/authz/${user.uid}/${permission}`)),
            eventKey ? objectVal(ref(this.db, `regions/${regionKey}/events/${eventKey}/authz/${user.uid}/${permission}`)) : of(false),
            defer(() => user.getIdTokenResult()).pipe(map(tokenResult => !!tokenResult?.claims['superAdmin']))
          ]).pipe(map(([hasRegionAuth, hasEventAuth, isSuperAdmin]) => {
            if (hasRegionAuth || hasEventAuth || isSuperAdmin) return true;

            return false;
          }));
        }
        return of(false);
      }),
    );
  }

  getUpdatableRoles(regionKey: string, eventKey?: string): Observable<string[]> {
    return user(this.auth).pipe(
      switchMap(async user => {
        if (!user) return [];

        const tokenResult = await user.getIdTokenResult();
        if (tokenResult?.claims['superAdmin']) {
          return Object.keys(await firstValueFrom(objectVal(ref(this.db, `permissions/roles`))) || {})
        };

        return firstValueFrom(objectVal<{[roleKey: string]: boolean}|null>(ref(this.db, `regions/${regionKey}/authz/${user.uid}/updateUsers`)).pipe(
          switchMap(regionRoles => 
            eventKey ? objectVal<{[roleKey: string]: boolean}|null>(ref(this.db, `regions/${regionKey}/events/${eventKey}/authz/${user.uid}/updateUsers`))
              .pipe(
                map(eventRoles => Object.keys({...regionRoles, ...eventRoles}))
              ) : of(Object.keys(regionRoles || {})))
        ));
      })
    );
  }

  hasAnyRole(regionKey: string, eventKey?: string, eventOnly?: true): Observable<boolean> {
    return  user(this.auth).pipe(
      switchMap(user => user ? combineLatest([
        objectVal<unknown>(ref(this.db, `regions/${regionKey}/authz/${user.uid}`)).pipe(map(authz => !!authz)),
        eventKey ? objectVal<unknown>(ref(this.db, `regions/${regionKey}/events/${eventKey}/authz/${user.uid}`)).pipe(map(authz => !!authz)) : of(false),
        this.isSuperAdmin(),
      ]).pipe(map(([region, event, isSuperAdmin]) => (region && !eventOnly) || event || isSuperAdmin)) : of(false)),
    );
  }

  getPendingRequests(regionKey: string, eventKey?: string) {
    if(!eventKey) return of([]);

    return combineLatest([
      this.getUpdatableRoles(regionKey, eventKey),
      this.isAuthorized(regionKey, eventKey, 'updateUsers').pipe(switchMap(a => a ? objectVal<AccessRequests>(ref(this.db, `regions/${regionKey}/events/${eventKey}/requests`)) : of(null))),
    ]).pipe(map(([updatableRoles, requests]) => {
      const result: (AccessRequest&{uid: string})[] = [];
      for(const [uid, r] of Object.entries(requests || {})) {
        if(updatableRoles.includes(r.role)) {
          result.push({...r, uid});
        }
      }

      result.sort((a,b) => a.timestamp - b.timestamp);

      return result;
    }));
  }

  getHighestRole(eventKey: string): Observable<string|undefined> {
    // return combineLatest([user(this.auth), this.roles$]).pipe(
    //   switchMap(([user, roles]) => {
    //     if (user) {
    //       return objectVal<{[permission: string]: boolean}>(ref(this.db, `events/${eventKey}/authz/${user.uid}`)).pipe(switchMap(async authz => {
    //         if (!authz) return undefined;

    //         for(const role of Object.values(roles).sort((a,b) => a.sort - b.sort)) {
    //           if(role.permissions.every(val => authz[val])) {
    //             return role.title;
    //           }
    //         }
    //         return 'Unknown role';
    //       }));
    //     }

    //     return of(undefined);
    //   }),
    // );

    return EMPTY;
  }

  isSuperAdmin(): Observable<boolean> {
    return user(this.auth).pipe(
      switchMap(user => user ? user.getIdTokenResult() : of(undefined)),
      map(tokenResult => !!tokenResult?.claims['superAdmin']));
  }
}
